import { setCookie } from "cookies-next";
import { useRouter } from "next/router";

import {
  RhButton,
  RhIcon,
  RhInputFormik,
  RhLabel,
  RhOTPInput,
  RhToast,
} from "@rhythm-ui/react";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import * as Yup from "yup";

import api from "@/services/api";

import { COOKIE, LOCAL_STORAGE, USER_ROLE } from "@/constants";

import useAuth from "@/hooks/useAuth";
import useFocusInputField from "@/hooks/useFocusInputField";

import getDomain from "@/helpers/getDomain";
import showToastMessages from "@/helpers/showToastMessages";

import PhoneFormik from "../formikInput/PhoneFormik";
import Button from "../utils/Button";

interface FormType {
  name: string;
  email: string;
  mobile__form_value: {
    dialCode: string;
    phoneNumber: string;
  };
}

const showOTPInputFor = {
  EMAIL: "EMAIL",
  PHONE: "PHONE",
};

const CandidateSignUpForm = () => {
  const authSessionKey = useRef<null | string>(null);

  const [showVerifyOTP, setShowVerifyOTP] = useState([] as string[]);

  // read for email in localstorage; this is in case the user has been
  // redirected from landing page and filling just email
  // in that case read the email on localstorage and prefill in the form

  const [enteredFormValue, setEnteredFormValue] = useState<FormType>({
    name: "",
    email: "",
    mobile__form_value: { dialCode: "+91", phoneNumber: "" },
  });

  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [isPhoneValidated, setIsPhoneValidated] = useState(false);

  const handleEdit = () => {
    setShowVerifyOTP([]);
  };

  const router = useRouter();

  const onSignupHandler = () => {
    localStorage.setItem(
      LOCAL_STORAGE.LAST_LOGIN_USER_TYPE,
      USER_ROLE.CANDIDATE
    );
    router.push("/login");
  };

  return (
    <>
      <h1>Sign up as a Candidate</h1>
      {showVerifyOTP.length === 0 ? (
        <span className=" mb-tsm text-light">
          A 6 digit OTP will be sent to your phone and email
        </span>
      ) : (
        <div className=" mb-tsm text-light flex flex-wrap items-center">
          <p className="!mr-0.5">{`A 6 digit OTP will be sent to `}</p>
          {!isPhoneValidated && (
            <div className="flex items-center">
              <p
                onClick={handleEdit}
                className="border-b border-dashed cursor-pointer"
              >
                {/* click interaction on p tag to make it easier for user in interactive display (touch screen) */}
                {enteredFormValue.mobile__form_value.dialCode}{" "}
                {enteredFormValue.mobile__form_value.phoneNumber}
              </p>
              <button
                className=" p-tpico rounded-full hover:bg-gray-100"
                onClick={handleEdit}
              >
                <RhIcon icon={"tabler:pencil"} />
              </button>
            </div>
          )}
          {showVerifyOTP.length === 2 && <p className="mr-0.5">and</p>}
          {!isEmailValidated && (
            <div className="flex items-center">
              <button
                onClick={handleEdit}
                className="border-b border-dashed cursor-pointer"
              >
                <p>{enteredFormValue.email}</p>
              </button>
              {/* click interaction on p tag to make it easier for user in interactive display (touch screen) */}
              <button
                className="ml-0.5 p-tpico rounded-full hover:bg-gray-100"
                onClick={handleEdit}
              >
                <RhIcon icon={"tabler:pencil"} />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-tsm flex flex-wrap flex-col gap-2">
        {showVerifyOTP.length === 0 ? (
          <PhoneEmailForm
            enteredFormValue={enteredFormValue}
            setEnteredFormValue={setEnteredFormValue}
            isEmailValidated={isEmailValidated}
            isPhoneValidated={isPhoneValidated}
            authSessionKey={authSessionKey}
            setShowVerifyOTP={setShowVerifyOTP}
          />
        ) : (
          <div className="flex flex-col">
            <OTPVerificationForm
              showVerifyOTP={showVerifyOTP}
              isEmailValidated={isEmailValidated}
              setIsEmailValidated={setIsEmailValidated}
              isPhoneValidated={isPhoneValidated}
              setIsPhoneValidated={setIsPhoneValidated}
              setShowVerifyOTP={setShowVerifyOTP}
              authSessionKey={authSessionKey}
              enteredFormValue={enteredFormValue}
            />
          </div>
        )}

        <div className="flex justify-between flex-col">
          <p className="text-center mt-t2xl">Already have account?</p>
          <RhButton
            layout="link"
            className="p-tnano mx-auto"
            onClick={onSignupHandler}
          >
            Sign in
          </RhButton>
        </div>
      </div>
      {/*  */}
    </>
  );
};

export default CandidateSignUpForm;

const PhoneEmailForm = ({
  enteredFormValue,
  setEnteredFormValue,
  isPhoneValidated,
  isEmailValidated,
  authSessionKey,
  setShowVerifyOTP,
}: {
  enteredFormValue: FormType;
  setEnteredFormValue: React.Dispatch<React.SetStateAction<FormType>>;
  isPhoneValidated: boolean;
  isEmailValidated: boolean;
  authSessionKey: React.MutableRefObject<string | null>;
  setShowVerifyOTP: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const [isOTPSentLoading, setIsOTPSentLoading] = useState(false);

  const phoneEmailValidationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    ...(!isEmailValidated && {
      email: Yup.string().email("Invalid email").required("Required"),
    }),
    ...(!isPhoneValidated && {
      mobile__form_value: Yup.object().shape({
        dialCode: Yup.string(),
        phoneNumber: Yup.string()
          .typeError("Required")
          .required("Required")
          .matches(/^\d+$/, "Phone number is invalid"),
        // .length(10, "Number must be 10 digit long"),
      }),
    }),
  });

  const handleSignupFormSubmit = async (formData: {
    name: string;
    email: string;
    mobile__form_value: { dialCode: string; phoneNumber: string };
  }) => {
    setIsOTPSentLoading(true);
    try {
      const res = await api.auth.postCandidateSignup({
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile__form_value.phoneNumber,
      });
      setEnteredFormValue(formData);
      authSessionKey.current = res.data.data.session_key;
      setShowVerifyOTP([showOTPInputFor.EMAIL, showOTPInputFor.PHONE]);
      RhToast.success("OTP sent your mobile and email", {
        position: "bottom-right",
      });
    } catch (error) {
      showToastMessages(error);
    }
    setIsOTPSentLoading(false);
  };

  const focusInputRef = useFocusInputField();

  return (
    <Formik
      enableReinitialize
      initialValues={enteredFormValue}
      validationSchema={phoneEmailValidationSchema}
      onSubmit={handleSignupFormSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <RhInputFormik name="name" label="Name" block ref={focusInputRef} />
          {!isEmailValidated && (
            <RhInputFormik name="email" label="Email" block />
          )}
          {!isPhoneValidated && (
            <>
              <RhLabel required>Phone</RhLabel>
              <PhoneFormik
                valueKey={"mobile__form_value"}
                values={values}
                setFormikFieldValue={setFieldValue}
              />
            </>
          )}
          <Button type="submit" loading={isOTPSentLoading} block>
            Request OTP
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const OTPVerificationForm = ({
  isEmailValidated,
  setIsEmailValidated,
  isPhoneValidated,
  setIsPhoneValidated,
  setShowVerifyOTP,
  showVerifyOTP,
  authSessionKey,
  enteredFormValue,
}: {
  isEmailValidated: boolean;
  setIsEmailValidated: React.Dispatch<React.SetStateAction<boolean>>;
  isPhoneValidated: boolean;
  setIsPhoneValidated: React.Dispatch<React.SetStateAction<boolean>>;
  setShowVerifyOTP: React.Dispatch<React.SetStateAction<string[]>>;
  showVerifyOTP: string[];
  authSessionKey: React.MutableRefObject<string | null>;
  enteredFormValue: FormType;
}) => {
  const { setUserAuth } = useAuth();
  const router = useRouter();

  const [emailOTP, setEmailOTP] = useState("");
  const [phoneOTP, setPhoneOTP] = useState("");

  const [isEmailOTPResent, setIsEmailOTPResent] = useState(true);
  const [isPhoneOTPResent, setIsPhoneOTPResent] = useState(true);

  const [isVerificationLoading, setVerificationLoading] = useState(false);

  const onOTPSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setVerificationLoading(true);
    if (!authSessionKey.current) return;
    try {
      const res = await api.auth.verifyCandidateOTP({
        ...(emailOTP && {
          email_otp: emailOTP,
        }),
        ...(phoneOTP && {
          mobile_otp: phoneOTP,
        }),
        session_key: authSessionKey.current,
      });

      const domain = getDomain();
      // verifying OTP
      setCookie(COOKIE.ZELACCESS, res.data.token.access, {
        domain,
        path: "/",
        sameSite: "strict",
      });
      setCookie(COOKIE.ZELREFRESH, res.data.token.refresh, {
        domain,
        path: "/",
        sameSite: "strict",
      });
      setCookie(COOKIE.ROLES, res.data.token.role.join("|"), {
        domain,
        path: "/",
        sameSite: "strict",
      });

      setUserAuth();

      if (res.data.token.new_candidate === true)
        router.push("/candidate/onboarding");
      else router.push("/jobs");
    } catch (error) {
      const isEmailVerified = error?.response?.data?.data?.email_verified;
      const isPhoneVerified = error?.response?.data?.data?.mobile_verified;
      if (isEmailVerified !== undefined || isPhoneVerified !== undefined) {
        setIsEmailValidated(isEmailVerified);
        setIsPhoneValidated(isPhoneVerified);
        setShowVerifyOTP([
          ...(!isEmailVerified ? [showOTPInputFor.EMAIL] : []),
          ...(!isPhoneVerified ? [showOTPInputFor.PHONE] : []),
        ]);
        setEmailOTP("");
        setPhoneOTP("");
        showToastMessages(error);
      } else {
        showToastMessages(error);
      }
      setVerificationLoading(false);
    }
  };

  const handlePhoneResend = async () => {
    if (!authSessionKey.current) return;

    setIsPhoneOTPResent(false);
    try {
      await api.auth.postCandidateSignup({
        name: enteredFormValue.name,
        mobile: enteredFormValue.mobile__form_value.phoneNumber,
        email: enteredFormValue.email,
        resend_mobile_otp: true,
        session_key: authSessionKey.current,
      });
      RhToast.success("OTP resent to mobile", {
        position: "bottom-right",
      });
      setPhoneOTP("");
    } catch (error) {
      showToastMessages(error);
    }
    setIsPhoneOTPResent(true);
  };

  const handleEmailResend = async () => {
    if (!authSessionKey.current) return;

    setIsEmailOTPResent(false);
    try {
      await api.auth.postCandidateSignup({
        name: enteredFormValue.name,
        mobile: enteredFormValue.mobile__form_value.phoneNumber,
        email: enteredFormValue.email,
        resend_email_otp: true,
        session_key: authSessionKey.current,
      });
      RhToast.success("OTP resent to email", {
        position: "bottom-right",
      });
      setEmailOTP("");
    } catch (error) {
      showToastMessages(error);
    }
    setIsEmailOTPResent(true);
  };

  return (
    <form onSubmit={onOTPSubmitHandler}>
      {!isEmailValidated ? (
        <>
          {showVerifyOTP.includes(showOTPInputFor.EMAIL) && (
            <>
              <RhLabel required className="mb-0">
                Email OTP
              </RhLabel>
              <div className="mb-tm2 max-w-full [&>.helper-text]:hidden">
                <RhOTPInput
                  className=""
                  length={6}
                  onChange={setEmailOTP}
                  value={emailOTP}
                />
                <button
                  type="button"
                  className="body-small border-b border-dashed text-hint !p-tpico !mt-tpico"
                  onClick={handleEmailResend}
                  loading={!isEmailOTPResent}
                >
                  Resend
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <p className="text-green-500">Email Verified</p>
      )}

      {!isPhoneValidated ? (
        <>
          <RhLabel required className="mb-0">
            Phone OTP
          </RhLabel>
          <div className="mb-tmini max-w-full [&>.helper-text]:hidden">
            <RhOTPInput
              length={6}
              onChange={setPhoneOTP}
              value={phoneOTP}
              className=""
            />

            <button
              type="button"
              className="body-small border-b border-dashed text-hint !p-tpico !mt-tpico"
              onClick={handlePhoneResend}
              loading={!isPhoneOTPResent}
            >
              Resend
            </button>
          </div>
        </>
      ) : (
        <p className="text-green-500">Phone Verified</p>
      )}

      <Button
        type="submit"
        disabled={
          (!isPhoneValidated && phoneOTP.length !== 6) ||
          (!isEmailValidated && emailOTP.length !== 6) ||
          isVerificationLoading
        }
        loading={isVerificationLoading}
        block
        className="mt-tsm"
      >
        Verify
      </Button>
    </form>
  );
};
